import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import type { Client } from "./api/types";
import AppShell from "./components/AppShell";
import AssistantDock from "./components/AssistantDock";
import AssistantPanel from "./components/AssistantPanel";
import ErrorBoundary from "./components/ErrorBoundary";
import { AssistantProvider } from "./lib/AssistantProvider";
import TopBar from "./components/TopBar";
import { data } from "./data";
import { firstName, useAuth } from "./lib/auth";
import { AuthProvider } from "./lib/AuthProvider";
import { isReady, someGated } from "./lib/flags";
// LoginPage stays eager: it is the first paint for a signed-out visitor, and a
// chunk fetch there would flash a loader before the one screen that has to be
// instant. The screens behind the sign in are split out, so a visitor downloads
// only the one they open, and the initial bundle stops carrying all eight.
import LoginPage from "./pages/LoginPage";
import "./styles/base.css";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AddClientPage = lazy(() => import("./pages/AddClientPage"));
const MatchListPage = lazy(() => import("./pages/MatchListPage"));
const RecordCallPage = lazy(() => import("./pages/RecordCallPage"));
const CastingRolesPage = lazy(() => import("./pages/CastingRolesPage"));
const ProjectDetailsPage = lazy(() => import("./pages/ProjectDetailsPage"));
const AssistantLogPage = lazy(() => import("./pages/AssistantLogPage"));
const UploadGridPage = lazy(() => import("./pages/UploadGridPage"));


/** Nothing renders until Firebase has said whether there is a session, and
 *  the app itself renders only when there is one. */
export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  // A brief blank rather than a spinner: Firebase answers from local storage
  // in a few milliseconds on a return visit, and a spinner that flashes for
  // one frame looks worse than nothing.
  if (loading) return null;
  if (!user) return <LoginPage />;
  return <Shell />;
}

function Shell() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsFailed, setClientsFailed] = useState(false);
  /** Remembered for the session. Somebody who collapsed it did so because
   *  they wanted the room, and expanding itself on every navigation would be
   *  the same annoyance in a different form. */
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem("castflow.sidebar.collapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem("castflow.sidebar.collapsed", collapsed ? "1" : "0");
    } catch {
      // Private windows and blocked storage both throw, and neither is a
      // reason to fail to draw the app.
    }
  }, [collapsed]);

  const reload = useCallback(() => {
    setClientsFailed(false);
    data
      .listClients()
      .then((cs) => {
        setClients(cs);
        setCurrentId((id) => id ?? cs[0]?.id ?? null);
      })
      .catch(() => {
        // Not the same thing as having no clients, and the sidebar showed
        // them identically. Against fixtures this could not happen; against
        // the API it is what an expired token, a CORS rule or a backend
        // restart all look like, and a manager seeing an empty list would
        // reasonably think their clients had been deleted.
        setClients([]);
        setClientsFailed(true);
      });
  }, []);

  useEffect(reload, [reload]);

  // The assistant rail belongs to the dashboard only. Every other screen
  // runs full width in the design.
  const onDashboard = useLocation().pathname === "/";
  const [currentId, setCurrentId] = useState<string | null>(null);

  return (
    // Above the shell, because the dashboard draws the assistant in the rail
    // and every other screen draws it in the dock. Those are two components,
    // so without this the conversation and the productions it answers from
    // belong to whichever one is currently mounted and are lost on the way
    // between screens.
    <AssistantProvider>
    <AppShell
      clients={clients}
      clientsFailed={clientsFailed}
      onRetryClients={reload}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((v) => !v)}
      topbar={
        <TopBar
          clients={clients}
          currentId={currentId}
          variant={onDashboard ? "dashboard" : "default"}
        />
      }
      // The dashboard keeps the rail it is drawn with. Every other screen is
      // full width in the design, so the assistant arrives there as a dock
      // that slides over rather than a column taken out of the page.
      rail={onDashboard ? <AssistantPanel /> : undefined}
    >
      {/* Around the screens rather than the whole app, so one page that
          cannot draw leaves the sidebar and the top bar in place and the
          reader can go somewhere else. Wrapped outside, a single throw took
          the entire interface and left a blank window. */}
      <ErrorBoundary>
      {/* One boundary for the lazily loaded screens. The fallback is the same
          quiet "state" line the pages use for their own loads, so a chunk fetch
          reads as the page loading rather than as something new. */}
      <Suspense fallback={<div className="state">Loading</div>}>
      <Routes>
        <Route
          path="/"
          element={
            <DashboardPage managerName={user ? firstName(user) : ""} />
          }
        />
        {isReady("add-client") && (
          <>
            <Route path="/clients/new" element={<AddClientPage onAdded={reload} />} />
            <Route path="/clients/:id/edit" element={<AddClientPage onAdded={reload} />} />
          </>
        )}
        {isReady("matches") && <Route path="/clients/:id" element={<MatchListPage />} />}
        {isReady("record-call") && (
          <Route path="/clients/:id/calls/new" element={<RecordCallPage clients={clients} />} />
        )}
        {isReady("roles") && <Route path="/roles" element={<CastingRolesPage />} />}
        {isReady("project") && <Route path="/roles/:id" element={<ProjectDetailsPage />} />}
        {/* With the assistant, because it is the assistant's own record and
            reads the same endpoint family. */}
        {isReady("assistant-log") && <Route path="/assistant/log" element={<AssistantLogPage />} />}
        {isReady("uploads") && <Route path="/uploads" element={<UploadGridPage />} />}
        <Route path="/settings" element={<Navigate to="/" replace />} />
        {/* A URL for a screen that is still gated lands on the dashboard
            rather than on "Page not found", since the route simply is not
            registered and there is nothing useful to say about it yet. */}
        <Route
          path="*"
          element={
            someGated ? <Navigate to="/" replace /> : <div className="state">Page not found.</div>
          }
        />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      {!onDashboard && <AssistantDock />}
    </AppShell>
    </AssistantProvider>
  );
}
